/**
 * The configuration screen.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * WORK_ORDER_M3_CONFIGURATION_AND_SURFACE.md section 4.2 screen 7, brought honest for
 * M5: the configuration endpoints are LIVE in prod, so the stale "not switched on"
 * banner and the dead prepared-layout copy are gone. What exists is wired: the
 * terminology list is read from GET /api/configuration/terminology and rendered as it
 * arrives. What does not exist yet - the decision-rule and calendar-enforcement
 * editors - is declared future work in the panel's own copy. No placeholder data.
 */

import { useMemo, useState } from "react";

import { probeConfigurationAccess } from "../api/capabilities";
import { ApiError } from "../api/client";
import {
  listTerminologyOverrides,
  readConfigurationAudit,
  type ConfigurationAuditEntry,
  type TerminologyOverride,
} from "../api/configuration";
import { readRatingScheme, setNoticeLeadDays, type VerificationTypeLead } from "../api/criticality";
import { ServerField } from "../lib/serverErrors";
import { zonedStamp } from "../lib/zonedTime";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { useToken } from "../auth/sessionContext";
import { strings, term } from "../brand/strings";
import { CONFIGURATION_PANELS, type ConfigurationPanel } from "../configuration/types";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { CitationChips } from "../components/Citations";
import { TaskClocksPanel } from "./ConfigurationClocks";
import { WebhookEndpointsPanel } from "./ConfigurationWebhooks";
import { AbsencePanel, EmptyState, LoadingLine, Section, SkeletonRows } from "./parts";
import { useQuery } from "./useQuery";

export function ConfigurationPage(): React.JSX.Element {
  const token = useToken();
  const allowed = useQuery(() => probeConfigurationAccess(token), [token]);
  const [panel, setPanel] = useState<ConfigurationPanel>("terminology");

  if (allowed.kind === "loading") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.configuration.heading}</h2>
        <LoadingLine label={strings.common.loading} />
      </>
    );
  }

  if (allowed.kind !== "ready" || !allowed.data) {
    return (
      <>
        <h2 className="q-shell__heading">{strings.configuration.heading}</h2>
        <AbsencePanel />
      </>
    );
  }

  return (
    <>
      <h2 className="q-shell__heading">{strings.configuration.heading}</h2>
      <p className="q-shell__lede">{strings.configuration.lede}</p>

      <div className="q-row q-gap-top" role="tablist" aria-label={strings.configuration.heading}>
        {CONFIGURATION_PANELS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={panel === name}
            className={`q-button q-button--quiet${panel === name ? " q-mini--primary" : ""}`}
            onClick={() => {
              setPanel(name);
            }}
          >
            {panelName(name)}
          </button>
        ))}
      </div>

      {panel === "terminology" && (
        <Section
          heading={strings.configuration.terminologyHeading}
          lede={strings.configuration.terminologyBody}
        >
          <TerminologyList />
        </Section>
      )}
      {panel === "verificationTypes" && (
        <Section
          heading={strings.configuration.verificationTypesHeading}
          lede={strings.configuration.verificationTypesBody}
        >
          <VerificationTypesPanel />
        </Section>
      )}
      {panel === "taskClocks" && (
        <Section
          heading={strings.configuration.taskClocksHeading}
          lede={strings.configuration.taskClocksBody}
        >
          <TaskClocksPanel />
        </Section>
      )}
      {panel === "webhooks" && (
        <Section
          heading={strings.configuration.webhooksHeading}
          lede={strings.configuration.webhooksBody}
        >
          <WebhookEndpointsPanel />
        </Section>
      )}
      {panel === "rules" && (
        <Section
          heading={strings.configuration.rulesHeading}
          lede={strings.configuration.rulesBody}
        >
          <FuturePanel />
        </Section>
      )}
      {panel === "calendar" && (
        <Section
          heading={strings.configuration.calendarHeading}
          lede={strings.configuration.calendarBody}
        >
          <FuturePanel />
        </Section>
      )}
      {panel === "audit" && (
        <Section
          heading={strings.configuration.auditHeading}
          lede={strings.configuration.auditBody}
        >
          <ConfigurationAuditPanel />
        </Section>
      )}
    </>
  );
}

function panelName(panel: ConfigurationPanel): string {
  switch (panel) {
    case "terminology":
      return strings.configuration.terminologyHeading;
    case "verificationTypes":
      return strings.configuration.verificationTypesHeading;
    case "taskClocks":
      return strings.configuration.taskClocksHeading;
    case "webhooks":
      return strings.configuration.webhooksHeading;
    case "rules":
      return strings.configuration.rulesHeading;
    case "calendar":
      return strings.configuration.calendarHeading;
    case "audit":
      return strings.configuration.auditHeading;
  }
}

/** A register card: its title, then its states or its DataTable (UX_SPEC 2.1). */
function RegisterCard({
  title,
  children,
}: {
  readonly title?: string | undefined;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="q-ui mb-(--q-s2) flex min-w-0 flex-col gap-3">
      {title !== undefined && (
        <h3 className="text-xs tracking-[0.16em] text-muted-foreground uppercase">{title}</h3>
      )}
      {children}
    </section>
  );
}

/** The terminology register columns: what each cell says in plain words. */
function terminologyColumns(): RegisterColumn<TerminologyOverride>[] {
  const copy = strings.configuration;
  return [
    {
      id: "term",
      label: copy.termLabel,
      text: (row) => row.objectKey,
      sortValue: (row) => row.objectKey,
      className: "font-mono",
      render: (row) => <span className="font-mono">{row.objectKey}</span>,
    },
    {
      id: "value",
      label: copy.valueLabel,
      text: (row) => row.value,
      sortValue: (row) => row.value,
    },
    {
      id: "locale",
      label: copy.localeLabel,
      text: (row) => row.locale,
      filter: true,
      className: "whitespace-nowrap",
    },
  ];
}

/** The live terminology overrides, read from the API and rendered as they arrived. */
function TerminologyList(): React.JSX.Element {
  const token = useToken();
  const overrides = useQuery((signal) => listTerminologyOverrides(token, signal), [token]);
  const columns = useMemo(() => terminologyColumns(), []);

  return (
    <RegisterCard>
      {overrides.kind === "loading" && <SkeletonRows />}
      {overrides.kind === "failed" && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {overrides.kind === "ready" &&
        (overrides.data.length === 0 ? (
          <div data-testid="configuration-terminology">
            <EmptyState
              title={strings.configuration.terminologyHeading}
              body={strings.configuration.terminologyEmpty}
            />
          </div>
        ) : (
          <div data-testid="configuration-terminology">
            <DataTable
              data={overrides.data}
              columns={columns}
              caption={strings.configuration.terminologyHeading}
              testId="configuration-terminology"
              getRowId={(row) => row.id}
            />
          </div>
        ))}
    </RegisterCard>
  );
}

/**
 * Wave INST ruling 3: the contractor's RFI notice lead time, per verification type. An
 * RFI proposed closer than it is refused unless it carries a short-notice justification.
 * Beside it, read only, the operator's notice of a planned assessment, which runs the
 * other way and is not this setting.
 */
function VerificationTypesPanel(): React.JSX.Element {
  const token = useToken();
  const [stamp, setStamp] = useState(0);
  const [editing, setEditing] = useState<VerificationTypeLead | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const scheme = useQuery((signal) => readRatingScheme(token, signal), [token, stamp]);
  const copy = strings.configuration;

  return (
    <RegisterCard>
      {toast !== null && (
        <ToastBar
          message={toast}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      {scheme.kind === "loading" && <SkeletonRows />}
      {(scheme.kind === "failed" || scheme.kind === "absence") && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {scheme.kind === "ready" && scheme.data.verificationTypes.length === 0 && (
        <div data-testid="configuration-verification-types">
          <EmptyState title={copy.verificationTypesHeading} body={copy.verificationTypesEmpty} />
        </div>
      )}
      {scheme.kind === "ready" && scheme.data.verificationTypes.length > 0 && (
        <div data-testid="configuration-verification-types">
          <DataTable
            data={scheme.data.verificationTypes}
            columns={verificationTypeColumns(setEditing, (message) => {
              setToast(message);
              setStamp((one) => one + 1);
            })}
            caption={copy.verificationTypesHeading}
            testId="verification-types"
            getRowId={(row) => row.id}
            getRowTestId={() => "verification-type-row"}
          />
        </div>
      )}
      {editing !== null && (
        <NoticeLeadDialog
          type={editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={(days) => {
            setEditing(null);
            setStamp((one) => one + 1);
            setToast(copy.noticeLeadSaved(editing.label, days));
          }}
        />
      )}
    </RegisterCard>
  );
}

/** The verification types register: the two lead times and the one act. */
function verificationTypeColumns(
  onEdit: (row: VerificationTypeLead) => void,
  onCited: (message: string) => void,
): RegisterColumn<VerificationTypeLead>[] {
  const copy = strings.configuration;
  const days = (value: number | null): string => (value === null ? copy.noLead : copy.days(value));
  return [
    {
      id: "type",
      label: copy.verificationTypeColumn,
      text: (row) => `${row.label} ${row.code}`,
      render: (row) => (
        <span>
          {row.label} <span className="font-mono text-muted-foreground">{row.code}</span>
        </span>
      ),
    },
    {
      id: "notice",
      label: copy.noticeLeadColumn,
      text: (row) => days(row.noticeLeadDays),
      sortValue: (row) => row.noticeLeadDays ?? -1,
      render: (row) => (
        <span data-testid="verification-type-notice">{days(row.noticeLeadDays)}</span>
      ),
    },
    {
      id: "notification",
      label: copy.notificationLeadColumn,
      text: (row) => days(row.notificationLeadDays),
      sortValue: (row) => row.notificationLeadDays ?? -1,
    },
    {
      id: "citations",
      label: strings.standards.citationsLabel,
      text: (row) =>
        row.citations
          .map((one) => strings.standards.chip(one.standardCode, one.editionLabel, one.clauseId))
          .join("; "),
      render: (row) => (
        <CitationChips
          citations={row.citations}
          target={{ type: "VERIFICATION_TYPE", id: row.id }}
          recordLabel={row.label}
          mayEdit
          testId="verification-type-citations"
          onChanged={onCited}
        />
      ),
    },
    {
      id: "act",
      label: strings.nav.actions,
      text: () => copy.noticeLeadEdit,
      render: (row) => (
        <button
          type="button"
          className="q-button q-button--quiet"
          data-testid="verification-type-edit"
          onClick={() => {
            onEdit(row);
          }}
        >
          {copy.noticeLeadEdit}
        </button>
      ),
    },
  ];
}

/** Set or clear one type's notice lead time; the refusal stays inside the dialog. */
function NoticeLeadDialog({
  type,
  onClose,
  onSaved,
}: {
  readonly type: VerificationTypeLead;
  readonly onClose: () => void;
  readonly onSaved: (days: number | null) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.configuration;
  const [value, setValue] = useState(
    type.noticeLeadDays === null ? "" : String(type.noticeLeadDays),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const trimmed = value.trim();
  const whole = trimmed === "" || /^\d+$/u.test(trimmed);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const saved = await setNoticeLeadDays(
        token,
        type.id,
        trimmed === "" ? null : Number(trimmed),
      );
      onSaved(saved.noticeLeadDays);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={copy.noticeLeadHeading(type.label)} onClose={onClose} error={error}>
      <p className="q-shell__lede">{copy.noticeLeadLede}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="notice-lead-days">
          {copy.noticeLeadLabel}
        </label>
        <p className="q-note" id="notice-lead-hint">
          {copy.noticeLeadHint}
        </p>
        <ServerField field="notice_lead_days">
          <input
            id="notice-lead-days"
            className="q-input"
            type="number"
            inputMode="numeric"
            min="0"
            max="365"
            step="1"
            data-testid="notice-lead-days"
            aria-describedby="notice-lead-hint"
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {!whole && (
        <p className="q-note" data-testid="notice-lead-whole">
          {copy.noticeLeadWhole}
        </p>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="notice-lead-save"
          disabled={busy || !whole}
          onClick={() => {
            void send();
          }}
        >
          {trimmed === "" ? copy.noticeLeadClear : copy.noticeLeadSave}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

/** A panel whose editor is declared future work, in its own copy. */
function FuturePanel(): React.JSX.Element {
  return (
    <div className="q-empty" data-testid="configuration-future">
      <p className="q-empty__body">{strings.configuration.futureWork}</p>
    </div>
  );
}

/**
 * Wave AUD frontend item 4: the configuration audit trail (GET /api/configuration/audit).
 * Shows only the CONFIGURATION chain and opens with its GENESIS entry on a tenant
 * registered after the deploy.
 */
function ConfigurationAuditPanel(): React.JSX.Element {
  const token = useToken();
  const audit = useQuery((signal) => readConfigurationAudit(token, signal), [token]);
  const columns = useMemo(() => configurationAuditColumns(), []);

  return (
    <RegisterCard>
      {audit.kind === "loading" && <SkeletonRows rows={4} columns={5} />}
      {audit.kind === "failed" && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {audit.kind === "ready" && (
        <div data-testid="configuration-audit">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span
              className={
                audit.data.chain.intact ? "q-chip q-chip--success" : "q-chip q-chip--danger"
              }
              data-testid="configuration-audit-chain"
              data-intact={String(audit.data.chain.intact)}
            >
              {audit.data.chain.intact
                ? strings.audit.configChainIntact(audit.data.chain.entriesChecked)
                : strings.audit.configChainBroken(
                    audit.data.chain.brokenAtSeq,
                    audit.data.chain.reason,
                  )}
            </span>
          </div>
          {audit.data.items.length === 0 ? (
            <EmptyState
              title={strings.configuration.auditHeading}
              body={strings.audit.historyEmpty}
              assignmentAware={false}
            />
          ) : (
            <DataTable
              data={audit.data.items}
              columns={columns}
              caption={strings.configuration.auditHeading}
              testId="configuration-audit-table"
              getRowId={(row) => row.id}
              getRowTestId={() => "configuration-audit-row"}
              pageSize={100}
              footer={false}
            />
          )}
        </div>
      )}
    </RegisterCard>
  );
}

function configurationAuditColumns(): RegisterColumn<ConfigurationAuditEntry>[] {
  const copy = strings.configuration;
  return [
    {
      id: "seq",
      label: copy.auditSeq,
      text: (row) => `#${String(row.seq)}`,
      sortValue: (row) => row.seq,
      render: (row) => (
        <span className="font-mono text-xs" data-testid="configuration-audit-seq">
          {`#${String(row.seq)}`}
        </span>
      ),
    },
    {
      id: "event",
      label: copy.auditEvent,
      text: (row) => `${row.eventType} ${term(row.eventType)}`,
      render: (row) => (
        <span data-testid="configuration-audit-event" data-event={row.eventType}>
          {row.eventType === "GENESIS" ? "GENESIS" : term(row.eventType)}
        </span>
      ),
    },
    {
      id: "object",
      label: copy.auditObject,
      text: (row) => [row.objectType, row.objectId ?? ""].filter((one) => one !== "").join(" "),
      render: (row) => (
        <span className="font-mono text-xs" data-testid="configuration-audit-object">
          {row.objectType}
          {row.objectId !== null && (
            <span className="ms-1 text-muted-foreground">{row.objectId}</span>
          )}
        </span>
      ),
    },
    {
      id: "actor",
      label: copy.auditActor,
      text: (row) => row.actorDisplayName ?? strings.audit.unknownActor,
      render: (row) => (
        <span data-testid="configuration-audit-actor">
          {row.actorDisplayName ?? strings.audit.unknownActor}
        </span>
      ),
    },
    {
      id: "when",
      label: copy.auditWhen,
      text: (row) => zonedStamp(row.createdAt),
      sortValue: (row) => row.createdAt,
      className: "whitespace-nowrap",
      render: (row) => (
        <span data-testid="configuration-audit-when">{zonedStamp(row.createdAt)}</span>
      ),
    },
  ];
}
